import React, { useState, FormEvent } from 'react';
import { useNavigate, useLocation, Link, Navigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useKleverWallet } from '../../context/KleverWalletContext';
import { AuthCard, Input, Button } from '../../components';
import IntroVideoModal from '../../components/IntroVideoModal';
import api from '../../utils/api';
import { FaEnvelope, FaWallet } from 'react-icons/fa';
import './Login.scss';

/** Normalise the `state.from` on the location to a single URL string,
 *  accepting both:
 *    - string ('/profile/foo') — ADR-007 signup-guard + anon AppBar
 *    - { pathname: string, search?: string } — older ProtectedRoute
 *  Returns null if neither shape matches so the caller falls back. */
function readFromLocationState(state: unknown): string | null {
  const raw = (state as any)?.from;
  if (typeof raw === 'string') return raw;
  if (raw && typeof raw === 'object' && typeof raw.pathname === 'string') {
    return raw.pathname + (raw.search || '');
  }
  return null;
}

type LoginMethod = 'email' | 'wallet' | null;

/**
 * Login page component
 * Supports both email/password and wallet-based authentication
 */
const Login: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, walletLogin, isLoading, error, isAuthenticated } = useAuth();
  const { connectWallet, signMessage, isConnecting, isExtensionInstalled, error: walletError } = useKleverWallet();

  const [loginMethod, setLoginMethod] = useState<LoginMethod>(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  // Get the page user was trying to access before login. Accepts both
  // shapes; ADR-007 producers pass strings, older ProtectedRoute passes
  // a { pathname } object.
  const from = readFromLocationState(location.state) || '/dashboard';

  if (isAuthenticated) return <Navigate to="/dashboard" replace />;

  const handleEmailSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!email || !password) {
      return;
    }

    try {
      const result = await login(email, password);

      if (result.meta.requestStatus === 'fulfilled') {
        // Check if user has a profile
        const userData = (result.payload as any)?.user;

        if (userData?.hasProfile === false) {
          // Redirect to profile creation if no profile exists
          navigate('/create-profile', { replace: true });
        } else {
          // Redirect to the page they were trying to access, or dashboard
          navigate(from, { replace: true });
        }
      }
    } catch (err) {
      console.error('Login failed:', err);
    }
  };

  const handleWalletLogin = async () => {
    try {
      const walletAddress = await connectWallet();

      // Server issues the challenge (single-use nonce + canonical message)
      // and persists it in auth.wallet_challenge. We sign exactly what the
      // server gave us and submit it back; walletLogin atomically consumes
      // the nonce. Closes auth-audit F-2 (welcome-bonus farming) + F-5
      // (signature replay).
      const { data: challenge } = await api.post<{ message: string }>(
        '/auth/wallet-challenge',
        { walletAddress },
      );
      const signature = await signMessage(challenge.message);

      const result = await walletLogin(walletAddress, signature, challenge.message);

      if (result.meta.requestStatus === 'fulfilled') {
        const userData = (result.payload as any)?.user;

        //console.debug('[Login] Wallet authentication successful');

        // Check if user has a profile
        if (userData?.hasProfile === false) {
          // Redirect to profile creation if no profile exists
          navigate('/create-profile', { replace: true });
        } else {
          // Redirect to the page they were trying to access, or dashboard
          navigate(from, { replace: true });
        }
      }
    } catch (err) {
      console.error('[Login] Wallet login failed:', err);
    }
  };

  // Method Selection View
  const renderMethodSelector = () => (
    <div className="login-method-selector">
      <h2 className="method-selector-title">Choose Login Method</h2>
      <p className="method-selector-subtitle">Select how you'd like to sign in</p>

      <div className="method-options">
        <button
          type="button"
          className="method-option"
          onClick={() => setLoginMethod('email')}
        >
          <div className="method-icon">
            <FaEnvelope />
          </div>
          <h3>Email & Password</h3>
          <p>Sign in with your registered email address</p>
        </button>

        <button
          type="button"
          className="method-option method-option--wallet"
          onClick={() => setLoginMethod('wallet')}
        >
          <div className="method-icon">
            <FaWallet />
          </div>
          <h3>Klever Wallet</h3>
          <p>Sign in or auto-create an account — no email needed</p>
          <span className="method-badge">New users welcome</span>
        </button>
      </div>
    </div>
  );

  // Email Login Form
  const renderEmailLogin = () => (
    <>
      <button
        type="button"
        className="back-button"
        onClick={() => setLoginMethod(null)}
      >
        ← Back to methods
      </button>

      <form onSubmit={handleEmailSubmit} className="login-form">
        {error && <div className="error-banner">{error}</div>}

        <Input
          label="Email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Enter your email"
          required
          disabled={isLoading}
          autoComplete="email"
        />

        <Input
          label="Password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Enter your password"
          required
          disabled={isLoading}
          autoComplete="current-password"
        />

        <div className="login-form-footer">
          <Link to="/forgot-password" className="forgot-password-link">
            Forgot password?
          </Link>
        </div>

        <Button
          type="submit"
          variant="primary"
          size="large"
          fullWidth
          isLoading={isLoading}
          disabled={!email || !password}
        >
          {isLoading ? 'Logging in...' : 'Log In'}
        </Button>
      </form>
    </>
  );

  // Wallet Login View
  const renderWalletLogin = () => (
    <>
      <button
        type="button"
        className="back-button"
        onClick={() => setLoginMethod(null)}
      >
        ← Back to methods
      </button>

      <div className="wallet-login">
        {walletError && <div className="error-banner">{walletError}</div>}

        {!isExtensionInstalled ? (
          <div className="wallet-install-prompt">
            <FaWallet className="wallet-icon-large" />
            <h3>Klever Wallet Required</h3>
            <p>
              Wallet login lets you sign in — or automatically create a Klyx account — without
              needing an email address. To get started, you'll need the Klever wallet:
            </p>
            <ul style={{ textAlign: 'left', margin: '1rem 0', paddingLeft: '1.5rem' }}>
              <li><strong>Mobile:</strong> Use the Klever Mobile Browser app</li>
              <li><strong>Desktop:</strong> Install the Klever Web Extension</li>
            </ul>
            <a
              href="https://chrome.google.com/webstore/detail/klever-wallet/ifclboecfhkjbpmhgehodcjpciihhmif"
              target="_blank"
              rel="noopener noreferrer"
              className="install-link"
            >
              Install Klever Extension
            </a>
          </div>
        ) : (
          <div className="wallet-connect-area">
            <FaWallet className="wallet-icon-large" />
            <h3>Sign In or Sign Up</h3>
            <p>Connect your Klever wallet to log in to your existing account, or create a new one instantly — no email required.</p>

            <Button
              variant="primary"
              size="large"
              fullWidth
              isLoading={isConnecting}
              onClick={handleWalletLogin}
            >
              {isConnecting ? 'Connecting...' : 'Connect Wallet'}
            </Button>

            <p className="wallet-info">
              First time here? A Klyx account will be created automatically and you'll receive a welcome bonus of 30 KLV.
            </p>
          </div>
        )}
      </div>
    </>
  );

  return (
    <>
      <IntroVideoModal storageKey="klyx-intro-login-dismissed" />
      <AuthCard
        title="Welcome to Klyx"
        subtitle={
          loginMethod === 'wallet'
            ? 'Sign in or create an account with your wallet'
            : loginMethod === 'email'
            ? 'Log in to start trading profiles'
            : ''
        }
        footerText="Don't have an account?"
        footerLink={{ text: 'Sign up', to: '/signup' }}
      >
        {loginMethod === null && renderMethodSelector()}
        {loginMethod === 'email' && renderEmailLogin()}
        {loginMethod === 'wallet' && renderWalletLogin()}
      </AuthCard>
    </>
  );
};

export default Login;
